<template>
  <v-card width="1200" class="mx-auto">
    <!-- Loading Indicator -->
    <v-progress-linear :indeterminate="isLoading" height="6" v-show="isLoading" color="primary"></v-progress-linear>

    <!-- Header -->
    <v-card-title class="primary white--text">
      <v-icon left>payment</v-icon>
      Formas de Pagamento
      <v-spacer></v-spacer>
      <v-btn @click="openDialog" color="white" outlined>
        <v-icon left>add</v-icon>
        Nova Forma de Pagamento
      </v-btn>
    </v-card-title>

    <!-- Search Bar -->
    <v-card-text style="width: 50%; border-radius: 15px; margin: 10px auto 0px;">
      <v-text-field v-model="search" append-icon="search" label="Pesquisar" single-line clearable outlined
        dense></v-text-field>
    </v-card-text>

    <!-- Data Table -->
    <v-data-table :headers="headers" :items="paymentMethods" :search="search" :loading="isLoading"
      loading-text="Carregando..." no-data-text="Nenhuma forma de pagamento encontrada" class="elevation-1">
      <template v-slot:item.actions="{ item }">
        <v-btn icon small color="error" @click="confirmDelete(item)">
          <v-icon>delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600" persistent>
      <v-card>
        <v-form ref="form" v-model="valid" @submit.prevent="save">
          <v-overlay :value="overlay" absolute>
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </v-overlay>

          <v-card-title class="primary white--text">
            <span class="text-h5">{{ formTitle }}</span>
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-autocomplete v-model="form.banco" :items="bancos" item-text="nome" item-value="pk_banco"
                    label="Banco*" :rules="requiredRule" outlined dense @change="filterContas"></v-autocomplete>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-autocomplete v-model="form.conta" :items="filteredContas" item-text="numeroConta"
                    item-value="pkContaBanco" label="Conta Bancária*" :rules="requiredRule" outlined
                    dense></v-autocomplete>
                </v-col>

                <v-col cols="12">
                  <v-autocomplete v-model="form.paymentType" :items="paymentTypes" item-text="descricao" item-value="id"
                    label="Tipo de Pagamento*" :rules="requiredRule" outlined dense></v-autocomplete>
                </v-col>

                <v-col cols="12" v-if="form.paymentType === 1">
                  <v-text-field v-model="form.reference" label="Referência*" :rules="referenceRules" outlined
                    dense></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog">Cancelar</v-btn>
            <v-btn color="primary" type="submit" :disabled="!valid || overlay">
              Salvar
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from '../../plugins/axios';
import Swal from 'sweetalert2';
const ls = require('local-storage');

export default {
  data: () => ({
    // UI State
    isLoading: false,
    overlay: false,
    dialog: false,
    valid: false,
    search: '',

    // Form Data
    form: {
      banco: null,
      conta: null,
      paymentType: null,
      reference: ''
    },

    // Data Lists
    paymentMethods: [],
    bancos: [],
    contas: [],
    filteredContas: [],
    paymentTypes: [],

    // Table Headers
    headers: [
      { text: 'Banco', value: 'banco' },
      { text: 'Conta', value: 'conta' },
      { text: 'Referência', value: 'referencia' },
      { text: 'Descrição', value: 'descricao' },
      { text: 'Autor', value: 'autor' },
      { text: 'Ações', value: 'actions', sortable: false, align: 'center' }
    ],

    // Validation Rules
    requiredRule: [v => !!v || 'Campo obrigatório'],
    referenceRules: [
      v => !!v || 'Campo obrigatório',
      v => (v && v.length >= 3) || 'Mínimo 3 caracteres',
      v => (v && v.length <= 50) || 'Máximo 50 caracteres'
    ]
  }),

  computed: {
    isAdm() {
      return ls.get('role') === 'ROLE_ADM';
    },
    formTitle() {
      return this.editedIndex === -1 ? 'Nova Forma de Pagamento' : 'Editar Forma de Pagamento';
    },
    user() {
      return ls.get('nomeUsuario');
    }
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      try {
        this.isLoading = true;
        await Promise.all([
          this.loadPaymentMethods(),
          this.loadBancos(),
          this.loadContas(),
          this.loadPaymentTypes()
        ]);
      } catch (error) {
        this.showError('Erro ao carregar dados');
      } finally {
        this.isLoading = false;
      }
    },

    async loadPaymentMethods() {
      this.paymentMethods = await axios.get('/tpasFindAll').then(res => res.data);
    },

    async loadBancos() {
      this.bancos = await axios.get('/bancosFindAll').then(res => res.data);
    },

    async loadContas() {
      this.contas = await axios.get('/conta-bancariaFindAll-escola').then(res => res.data);
    },

    async loadPaymentTypes() {
      this.paymentTypes = await axios.get('/listarFormasPagamento').then(res => res.data);
    },

    filterContas() {
      this.filteredContas = this.contas.filter(c => c.pk_banco === this.form.banco);
      this.form.conta = null;
    },

    openDialog() {
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.$refs.form.reset();
      this.form.reference = '';
    },

    async save() {
      if (!this.$refs.form.validate()) return;

      try {
        this.overlay = true;

        const banco = this.bancos.find(b => b.pk_banco === this.form.banco);
        const conta = this.contas.find(c => c.pkContaBanco === this.form.conta);
        const paymentType = this.paymentTypes.find(p => p.id === this.form.paymentType);

        const params = {
          banco: banco.nome,
          conta: conta.numeroConta,
          banco_id: banco.pk_banco,
          conta_id: conta.pkContaBanco,
          referencia: this.getDescription(paymentType.id),
          descricao: this.getReference(paymentType.id, banco.sigla),
          autor: this.user,
          formaPagamento: paymentType.id
        };

        const result = await axios.post('/tpaInsertOrUpdate', params);

        if (result.data.affectedRows >= 1) {
          this.showSuccess('Forma de pagamento salva com sucesso');
          this.closeDialog();
          this.loadPaymentMethods();
        }
      } catch (error) {
        if (error.toString().includes('409')) {
          this.showWarning('Forma de pagamento já existe');
        } else {
          this.showError('Erro ao salvar forma de pagamento');
        }
      } finally {
        this.overlay = false;
      }
    },

    getDescription(paymentTypeId) {
      const types = {
        1: this.form.reference,
        2: 'DEPÓSITO BANCÁRIO',
        3: 'TRANSFERÊNCIA BANCÁRIA',
        4: 'CHEQUE'
      };
      return types[paymentTypeId] || '';
    },

    getReference(paymentTypeId, sigla) {
      const conta = this.contas.find(c => c.pkContaBanco === this.form.conta);
      const types = {
        1: `TPA-${this.form.reference}-${sigla}`,
        2: `DEPOSITO-${conta.numeroConta}-${sigla}`,
        3: `TRANSFERÊNCIA-${conta.numeroConta}-${sigla}`,
        4: `CHEQUE-${conta.numeroConta}-${sigla}`
      };
      return types[paymentTypeId] || '';
    },

    confirmDelete(item) {
      Swal.fire({
        title: 'Confirmar exclusão?',
        text: "Esta ação não pode ser desfeita!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar'
      }).then(result => {
        if (result.isConfirmed) {
          this.deleteItem(item);
        }
      });
    },

    async deleteItem(item) {
      try {
        this.isLoading = true;
        const result = await axios.post('eliminarTPA', item);

        if (result.data.affectedRows >= 1) {
          this.showSuccess('Forma de pagamento excluída com sucesso');
          this.loadPaymentMethods();
        }
      } catch (error) {
        this.showError('Erro ao excluir forma de pagamento');
      } finally {
        this.isLoading = false;
      }
    },

    showSuccess(message) {
      Swal.fire('Sucesso!', message, 'success');
    },

    showWarning(message) {
      Swal.fire('Atenção!', message, 'warning');
    },

    showError(message) {
      Swal.fire('Erro!', message, 'error');
    }
  }
};
</script>

<style scoped>
.primary {
  background-color: #006699 !important;
}

.v-card {
  border-radius: 8px;
  overflow: hidden;
  margin-top: 20px;
}

.v-data-table {
  margin-top: 16px;
}
</style>